/**
 * ReactorSystemSVG — 계통 개요 그림. **KAERI 공식 계통도**(`docs/SMR_도식.pptx`, 2026-10-01 수령)를
 * 그대로 그린다: 도형은 생성기가 원본에서 옮긴 `systemDiagram.generated.ts`, 도형이 어느 계통인지는
 * `systemDiagramLayers.ts` 선언이 정한다. 그림의 계통 요소(`data-scope-id`)는 scope 카탈로그
 * (`types/scope.ts`)의 계통만 가리킨다 — 선택기에서 고른 계통이 정확히 그 요소들로 켜지고, 요소를
 * 클릭하면 정확히 그 계통이 토글된다(검출기 `__tests__/reactorSystemSvg.test.tsx`).
 *
 * 그전 그림은 우리가 임의로 그린 일체형 PWR 일반형이었다(계통 20). 공식 목록은 Primary 13·Secondary 5·
 * BOP 4 이고, 그림은 원본의 색(증기 주황·냉각수 청·전력 노랑·축 회색·격납 점선)으로 서며 배경도 원본의
 * 어두운 HMI 바탕을 그대로 깐다(화면 테마와 무관).
 *
 * 켜짐(highlight): 그 계통의 선·외곽·글자를 그룹 색(`scopeTheme`)으로 바꾼다 — 공식 자료의 그룹별
 * 강조 슬라이드와 같은 보임새. 채움은 선과 같은 색일 때(화살촉)만 바꾸고 풀·노심 같은 면은 그대로 둔다.
 * 흐림(dimmed): 불투명도를 낮춘다. 켜진 계통이 하나라도 있으면 나머지는 조금 흐려져 켜진 것이 도드라진다.
 */

import React, { useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { SCOPE_COLORS } from '@/features/scope/scopeTheme';
import { isKnownScope, scopeLabel, svgComponentGroup, type ReactorComponentId } from '@/types/scope';
import { SYSTEM_DIAGRAM } from './systemDiagram.generated';
import { diagramRoleOf, isSystemLabel, type DiagramRole } from './systemDiagramLayers';
import type { DiagramShape } from './systemDiagramModel';

// 요소 ID 타입 — 카탈로그(types/scope.ts)의 계통 id 와 동일
export type { ReactorComponentId } from '@/types/scope';

interface ReactorSystemSVGProps {
  /** 하이라이트할 요소(=계통) ID 배열 */
  highlightedComponents: ReactorComponentId[];
  /**
   * 흐리게 표시할 요소 ID 배열 — hover 대비 강조용 (#282).
   * 하이라이트와 겹치면 하이라이트가 우선한다.
   */
  dimmedComponents?: ReactorComponentId[];
  /** 요소 호버 시 콜백 */
  onComponentHover?: (id: ReactorComponentId | null) => void;
  /** 요소 클릭 시 콜백 */
  onComponentClick?: (id: ReactorComponentId) => void;
  /** SVG 너비 */
  width?: number | string;
  /** SVG 높이 */
  height?: number | string;
  /** 그릴 글자 층 — `all`(기본): 원본 그대로 · `system`: 계통 이름만(`DIAGRAM_NAME_LABELS`), `SYSTEM_LABEL_EMPHASIS` 배로 키워서(창을 줄여도 읽히게) */
  labels?: DiagramLabelMode;
}

/** 글자 층 선택 — 원본 전부 또는 계통 이름만 */
export type DiagramLabelMode = 'all' | 'system';

/** 계통 이름만 그릴 때의 글자 배율 — 설명 글자가 빠진 자리를 이름이 쓴다. 공식 그림의 이름 글자 22개(13.65~19.95px, 중앙값 18.91px)는
 *  1678px 보기 상자 기준이라 그림을 1000px 아래로 줄이면 중앙값이 11px 아래로 떨어진다; 1.5배면 그림 폭 ~706px 에서
 *  8.6(CORE)~12.6(RPV·PZR·CCS)px, 중앙값 11.9px(생성본에서 센 값; 전 글자 51개를 배율 없이 그리면 중앙값 7.5px) */
export const SYSTEM_LABEL_EMPHASIS = 1.5;

const FONT_FAMILY = '"Malgun Gothic", "Apple SD Gothic Neo", "Noto Sans KR", sans-serif';
/** 켜진 계통이 있을 때 나머지의 불투명도 / 명시적으로 흐린 요소의 불투명도 */
const REST_OPACITY = 0.55;
const DIM_OPACITY = 0.3;

/**
 * 칠하는 순서는 **원본 순서 그대로**다 — 계통별로 모아 그리면 겹치는 도형(노심 위의 유로 화살표, 복수기 안의
 * 냉각수 관, 교차 가림)의 앞뒤가 바뀌어 공식 그림과 달라진다(Copilot 지적). 그래서 원본 순서에서 같은
 * 계통이 이어지는 구간(run)마다 `<g data-scope-id>` 하나를 둔다 — 한 계통이 여러 묶음일 수 있고, 켜짐·흐림·
 * 클릭·hover 는 묶음이 아니라 계통 단위로 판정한다(이벤트는 svg 에서 위임받아 `data-scope-id` 로 읽는다).
 */
interface DiagramRun {
  role: DiagramRole;
  shapes: DiagramShape[];
  /** 그 계통의 첫 묶음인가 — 키보드 초점은 계통마다 하나(첫 묶음)에만 둔다 */
  first: boolean;
}

const DIAGRAM_RUNS: readonly DiagramRun[] = (() => {
  const runs: DiagramRun[] = [];
  const seen = new Set<DiagramRole>();
  for (const shape of SYSTEM_DIAGRAM.shapes) {
    const role = diagramRoleOf(shape.name);
    const last = runs[runs.length - 1];
    if (last && last.role === role) last.shapes.push(shape);
    else {
      runs.push({ role, shapes: [shape], first: !seen.has(role) });
      seen.add(role);
    }
  }
  return runs;
})();

/** 선만 있는 가는 도형의 **보이지 않는 넓은 쌍둥이** — 4 px 배관을 마우스로 집기 어렵다(Copilot 지적) */
const HIT_STROKE = 14;

const transformOf = (s: DiagramShape) =>
  s.flipH || s.flipV
    ? `translate(${s.x + (s.flipH ? s.w : 0)} ${s.y + (s.flipV ? s.h : 0)}) scale(${s.flipH ? -1 : 1} ${s.flipV ? -1 : 1})`
    : `translate(${s.x} ${s.y})`;

function Shape({ shape, accent, hitTargets, labelMode }: { shape: DiagramShape; accent: string | null; hitTargets: boolean; labelMode: DiagramLabelMode }) {
  const s = shape;
  // 배경색으로 그은 선은 가림막(비접속 교차의 끊김)이다 — 켜져도 색을 입히면 교차에 색 덩어리가 생긴다(Copilot)
  const mask = s.stroke === SYSTEM_DIAGRAM.background;
  const stroke = s.stroke && accent && !mask ? accent : s.stroke;
  // 채움은 선과 같은 색(화살촉 같은 선의 일부)일 때만 강조색 — 풀·노심·상자 바탕은 그대로
  const fill = s.fill && accent && s.fill === s.stroke ? accent : s.fill;
  const common = {
    fill: fill ?? 'none',
    stroke: stroke ?? 'none',
    strokeWidth: s.stroke ? s.strokeWidth : 0,
    strokeDasharray: s.dash ? s.dash.join(' ') : undefined,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    transform: transformOf(s),
  };
  let geometry: React.ReactNode;
  if (s.kind === 'path') geometry = <path d={s.d} {...common} />;
  else if (s.kind === 'rect') geometry = <rect width={s.w} height={s.h} {...common} />;
  else if (s.kind === 'roundRect') geometry = <rect width={s.w} height={s.h} rx={s.rx} {...common} />;
  else geometry = <ellipse cx={s.w / 2} cy={s.h / 2} rx={s.w / 2} ry={s.h / 2} {...common} />;
  // 선만 있는 경로(배관·화살표 선)에는 투명한 넓은 쌍둥이를 아래에 깔아 집기 쉽게 — 보이지 않는다
  const hit = hitTargets && s.kind === 'path' && s.stroke && !s.fill
    ? <path d={s.d} fill="none" stroke="transparent" strokeWidth={HIT_STROKE} strokeLinecap="round" strokeLinejoin="round" transform={common.transform} data-hit-target />
    : null;
  if (!s.text || (labelMode === 'system' && !isSystemLabel(s))) return <>{hit}{geometry}</>;
  const t = s.text;
  const size = labelMode === 'system' ? t.size * SYSTEM_LABEL_EMPHASIS : t.size;
  const lineHeight = size * 1.2;
  const anchorX = t.align === 'ctr' ? s.w / 2 : t.align === 'r' ? s.w : 0;
  const top = t.anchor === 'ctr' ? s.h / 2 - (t.lines.length * lineHeight) / 2 : t.anchor === 'b' ? s.h - t.lines.length * lineHeight : 0;
  return (
    <>
      {hit}
      {geometry}
      {t.lines.map((line, i) => (
        <text
          key={i}
          x={anchorX}
          y={top + i * lineHeight + size * 0.85}
          fontSize={size}
          fontFamily={FONT_FAMILY}
          fontWeight={t.bold ? 700 : 400}
          fill={accent ?? t.color ?? '#F0F3F7'}
          textAnchor={t.align === 'ctr' ? 'middle' : t.align === 'r' ? 'end' : 'start'}
          transform={`translate(${s.x} ${s.y})`}
        >
          {line}
        </text>
      ))}
    </>
  );
}

const ReactorSystemSVG: React.FC<ReactorSystemSVGProps> = ({
  highlightedComponents,
  dimmedComponents = [],
  onComponentHover,
  onComponentClick,
  width = '100%',
  height = 'auto',
  labels = 'all',
}) => {
  // 카탈로그 밖 id 는 그리지 않는다 — 호출부는 svgComponentsForScopes 로 거르지만 여기서도 지어내지 않는다
  const highlighted = useMemo(() => new Set<string>(highlightedComponents.filter(isKnownScope)), [highlightedComponents]);
  const dimmed = useMemo(() => new Set<string>(dimmedComponents), [dimmedComponents]);
  const anyHighlight = highlighted.size > 0;
  const interactive = !!(onComponentHover || onComponentClick);
  const [vx, vy, vw, vh] = SYSTEM_DIAGRAM.viewBox;
  const hoveredRef = useRef<ReactorComponentId | null>(null);

  /** 이벤트의 계통 — 묶음(run)이 아니라 계통으로 판정하므로 한 계통의 여러 묶음 사이를 오가도 깜빡이지 않는다 */
  const systemAt = (target: EventTarget | null): ReactorComponentId | null => {
    const el = (target as Element | null)?.closest?.('[data-scope-id]');
    return (el?.getAttribute('data-scope-id') as ReactorComponentId | null) ?? null;
  };
  const hover = (id: ReactorComponentId | null) => {
    if (!onComponentHover || hoveredRef.current === id) return;
    hoveredRef.current = id;
    onComponentHover(id);
  };

  return (
    <Box
      sx={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 키보드 초점 고리 — ScopeSelector 의 칩과 같은 규약
        '& [data-scope-id]:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <svg
        viewBox={`${vx} ${vy} ${vw} ${vh}`}
        width="100%"
        height="100%"
        style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', background: SYSTEM_DIAGRAM.background, borderRadius: 8 }}
        role="group"
        aria-label="SMR 계통 개요"
        onMouseOver={onComponentHover ? (e) => hover(systemAt(e.target)) : undefined}
        onMouseLeave={onComponentHover ? () => hover(null) : undefined}
        onClick={onComponentClick ? (e) => { const id = systemAt(e.target); if (id) onComponentClick(id); } : undefined}
        onKeyDown={onComponentClick ? (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          const id = systemAt(e.target);
          if (id) { e.preventDefault(); onComponentClick(id); }
        } : undefined}
      >
        {DIAGRAM_RUNS.map((run, i) => {
          if (run.role === 'frame') {
            // 계통 이름만 그릴 때는 틀(범례 아이콘·범례 글자)도 통째로 뺀다 — 글자 없는 범례 기호만 남으면 뜻 없는 점이 된다
            if (labels === 'system') return null;
            return (
              <g key={i} data-diagram-frame style={{ pointerEvents: 'none' }} opacity={anyHighlight ? REST_OPACITY : 1}>
                {run.shapes.map((s) => <Shape key={s.name} shape={s} accent={null} hitTargets={false} labelMode={labels} />)}
              </g>
            );
          }
          const id = run.role;
          const on = highlighted.has(id);
          const accent = on ? SCOPE_COLORS[svgComponentGroup(id)].color : null;
          const opacity = dimmed.has(id) && !on ? DIM_OPACITY : anyHighlight && !on ? REST_OPACITY : 1;
          // 키보드 초점은 계통마다 첫 묶음에 — 이름·눌림 상태를 읽어 준다. 누를 수 있을 때(onComponentClick)만 —
          // hover 만 받는 화면에 눌리지 않는 버튼을 내놓지 않는다(Copilot)
          const focusProps = onComponentClick && run.first
            ? { tabIndex: 0, role: 'button' as const, 'aria-label': scopeLabel(id), 'aria-pressed': on }
            : {};
          return (
            <g
              key={i}
              data-scope-id={id}
              data-highlighted={on ? 'true' : undefined}
              opacity={opacity}
              style={{ cursor: interactive ? 'pointer' : 'default', transition: 'opacity 150ms' }}
              {...focusProps}
            >
              {run.shapes.map((s) => <Shape key={s.name} shape={s} accent={accent} hitTargets={interactive} labelMode={labels} />)}
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

export default ReactorSystemSVG;
